/**
 * A condição montada por escolhas, sem React e sem rede.
 *
 * A pessoa escolhe o campo, "é" ou "não é" e um dos valores que o CRM mandou;
 * daqui sai a expressão que o serviço avalia (`message.author_role = 'contact'`).
 * O caminho inverso lê a expressão salva de volta em escolhas, para a mesma
 * automação abrir do jeito que foi montada. O que não cabe nesse formato (uma
 * expressão escrita à mão) não é lido: a tela mostra o texto como está.
 */

import type { CampoDoEvento } from '@cliqcrm/contracts';

export type Operador = 'igual' | 'diferente';

export interface Regra {
  readonly caminho: string;
  readonly operador: Operador;
  /** O valor como texto; no `sim_nao`, `true` ou `false`. */
  readonly valor: string;
}

const SIMBOLO: Record<Operador, string> = { igual: '=', diferente: '!=' };

/**
 * O valor como literal do JSONata. Aspas duplas e os escapes do JSON: o JSONata
 * não aceita `\'`, e um valor com apóstrofo (D'Ávila) quebraria a expressão.
 */
function literal(regra: Regra, campo: CampoDoEvento | undefined): string {
  if (campo?.tipo === 'sim_nao') return regra.valor === 'true' ? 'true' : 'false';
  return JSON.stringify(regra.valor);
}

/** A expressão das regras, ligadas por "e". Sem regra, nenhuma condição. */
export function expressaoDasRegras(
  regras: readonly Regra[],
  campos: readonly CampoDoEvento[],
): string {
  return regras
    .filter((r) => r.caminho && r.valor !== '')
    .map((r) => {
      const campo = campos.find((c) => c.caminho === r.caminho);
      return `${r.caminho} ${SIMBOLO[r.operador]} ${literal(r, campo)}`;
    })
    .join(' and ');
}

const UMA_REGRA = /^([A-Za-z_][\w.]*)\s*(!=|=)\s*("(?:[^"\\]|\\.)*"|'[^'\\]*'|true|false)$/;

/**
 * As partes ligadas por "and", sem cortar dentro de um texto entre aspas: o
 * valor "Compras and Vendas" é um valor só.
 */
function partes(texto: string): string[] {
  const saida: string[] = [];
  let aspa: string | null = null;
  let inicio = 0;
  for (let i = 0; i < texto.length; i++) {
    const c = texto[i]!;
    if (aspa) {
      if (c === '\\') i++;
      else if (c === aspa) aspa = null;
    } else if (c === '"' || c === "'") {
      aspa = c;
    } else if (/\s/.test(c) && /^\s+and\s/.test(texto.slice(i))) {
      saida.push(texto.slice(inicio, i));
      const m = /^\s+and\s+/.exec(texto.slice(i))!;
      i += m[0].length - 1;
      inicio = i + 1;
    }
  }
  saida.push(texto.slice(inicio));
  return saida;
}

function valorDoLiteral(bruto: string): string | null {
  if (bruto.startsWith('"')) {
    try {
      return JSON.parse(bruto) as string;
    } catch {
      return null;
    }
  }
  // Aspas simples sem escape: as expressões escritas à mão antes das escolhas.
  return bruto.slice(1, -1);
}

/**
 * As regras de uma expressão salva, ou `null` quando ela não é deste formato
 * (outro operador, um campo que o evento não traz, uma conta): essa fica como
 * texto, e a tela não a reescreve sem a pessoa pedir.
 */
export function regrasDaExpressao(
  expressao: string,
  campos: readonly CampoDoEvento[],
): readonly Regra[] | null {
  const texto = expressao.trim();
  if (!texto) return [];
  const regras: Regra[] = [];
  for (const parte of partes(texto)) {
    const m = UMA_REGRA.exec(parte.trim());
    if (!m) return null;
    const [, caminho, simbolo, bruto] = m as unknown as [string, string, string, string];
    const campo = campos.find((c) => c.caminho === caminho);
    if (!campo) return null;
    const ehBooleano = bruto === 'true' || bruto === 'false';
    if (ehBooleano !== (campo.tipo === 'sim_nao')) return null;
    const valor = ehBooleano ? bruto : valorDoLiteral(bruto);
    if (valor === null) return null;
    regras.push({ caminho, operador: simbolo === '=' ? 'igual' : 'diferente', valor });
  }
  return regras;
}

/** A regra nova: o primeiro campo ainda não usado, com o primeiro valor dele. */
export function regraNova(
  campos: readonly CampoDoEvento[],
  regras: readonly Regra[],
): Regra | null {
  const livre = campos.find((c) => !regras.some((r) => r.caminho === c.caminho)) ?? campos[0];
  if (!livre) return null;
  return { caminho: livre.caminho, operador: 'igual', valor: primeiroValor(livre) };
}

export function primeiroValor(campo: CampoDoEvento): string {
  if (campo.tipo === 'sim_nao') return 'true';
  return campo.opcoes[0]?.valor ?? '';
}

/** O nome de um valor, para o resumo e para os casos da escolha. */
export function rotuloDoValor(campo: CampoDoEvento | undefined, valor: string): string {
  if (campo?.tipo === 'sim_nao') return valor === 'true' ? 'Sim' : 'Não';
  return campo?.opcoes.find((o) => o.valor === valor)?.rotulo ?? valor;
}

/** A frase da condição, para a pessoa conferir o que montou. */
export function resumoDasRegras(
  regras: readonly Regra[],
  campos: readonly CampoDoEvento[],
): string {
  return regras
    .filter((r) => r.valor !== '')
    .map((r) => {
      const campo = campos.find((c) => c.caminho === r.caminho);
      const nome = campo?.titulo ?? r.caminho;
      return `${nome} ${r.operador === 'igual' ? 'é' : 'não é'} “${rotuloDoValor(campo, r.valor)}”`;
    })
    .join(' e ');
}
